// layout.ts — the documentation page's pane preference, remembered.
//
// Two ways to read a repository, and which one is right is a property of the
// person and their screen rather than of the data, so it is a preference and
// not a heuristic on viewport width.
//
// `split` keeps the tree beside the document, which is the better default: a
// directory's summary is assembled from its children's, so the question asked
// of any page is "assembled from what?" and the answer is the subtree still on
// screen next to it.
//
// `full` gives the whole width to one thing at a time, at a larger size. That
// is the mode for actually reading — walking a tree of two thousand nodes, or
// sitting with one document — where the other pane is not context, it is a
// column of text competing for the same eyes.
//
// Persisted because a reading preference that resets on every navigation is
// not a preference. localStorage rather than the URL: it describes the reader,
// not the resource, so it should not travel in a link someone shares.
import { useCallback, useEffect, useState } from 'react';

export type LayoutMode = 'split' | 'full';

const STORAGE_KEY = 'docdaemon_docs_layout';

function readStored(): LayoutMode {
  // Guarded: a browser with storage disabled throws on access rather than
  // returning null, and that must not take the documentation page down.
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'full' ? 'full' : 'split';
  } catch {
    return 'split';
  }
}

// useLayoutMode returns the current mode and a setter that persists it.
export function useLayoutMode(): [LayoutMode, (next: LayoutMode) => void] {
  const [mode, setMode] = useState<LayoutMode>(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // A preference that cannot be saved still applies for this session.
    }
  }, [mode]);

  const set = useCallback((next: LayoutMode) => setMode(next), []);
  return [mode, set];
}
